import type { AnnouncementFlag, User } from '@/types';

const HEX_COLOR = /^#[A-Fa-f0-9]{6}([A-Fa-f0-9]{2})?$/;

/**
 * BE-supplied button color, or undefined. The value lands in an inline style;
 * the BE hex-checks only on its webhook, so admin-panel writes are re-checked here.
 */
export function safeHexColor(value: string | null | undefined): string | undefined {
  return value && HEX_COLOR.test(value) ? value : undefined;
}

/** Most steps the release tour shows; the rest are reached from /whats-new. */
export const TOUR_MAX_STEPS = 5;

/**
 * Flags the web client requests. BE flags are a union ("android users OR
 * creators"), so `creator` must only be sent for users who are creators —
 * otherwise an Android+creator item matches a web non-creator.
 * `is_creator` is the same channel-ownership check the BE gate uses.
 */
export function requestFlags(
  user: Pick<User, 'is_creator'> | null | undefined
): AnnouncementFlag[] {
  return user?.is_creator ? ['web', 'creator'] : ['web'];
}

/** Published after the user's read cursor. A missing cursor means nothing was ever read. */
export function isUnreadAnnouncement(
  publishedAt: string,
  readAt: string | null | undefined
): boolean {
  if (!readAt) return true;
  return Date.parse(publishedAt) > Date.parse(readAt);
}

export type PopupSelection<T> =
  | { kind: 'none' }
  | { kind: 'single'; item: T }
  | { kind: 'tour'; items: T[]; overflowCount: number };

/** 1 unread → single headline; 2+ → tour capped at TOUR_MAX_STEPS. Input is newest first. */
export function selectPopup<T>(unread: readonly T[]): PopupSelection<T> {
  const [first] = unread;
  if (first === undefined) return { kind: 'none' };
  if (unread.length === 1) return { kind: 'single', item: first };
  return {
    kind: 'tour',
    items: unread.slice(0, TOUR_MAX_STEPS),
    overflowCount: Math.max(0, unread.length - TOUR_MAX_STEPS),
  };
}

/**
 * Newest `published_at` among what the user was shown — sent to mark-read as
 * `last_shown_at` so anything published after the fetch stays unread.
 */
export function newestPublishedAt(items: readonly { published_at: string }[]): string | undefined {
  let newest: { iso: string; ms: number } | undefined;
  for (const { published_at: iso } of items) {
    const ms = Date.parse(iso);
    if (!Number.isNaN(ms) && (newest === undefined || ms > newest.ms)) newest = { iso, ms };
  }
  return newest?.iso;
}

/** Later of two cursors — mirrors the BE's GREATEST() so the client never moves it back. */
export function laterCursor(current: string | null | undefined, next: string): string {
  if (!current) return next;
  return Date.parse(next) > Date.parse(current) ? next : current;
}
